'use client';
import { memo, useId, useMemo, useState } from 'react';
import { langForPath, parseDiffRow } from '../../lib/codeDiff';
import { highlightCode, type CodeTokenKind } from '../../lib/codeHighlight';
import { interpolate, useTranslation } from '../../lib/i18n';
import { MorePill } from '../../components/ui/MorePill';
import { CodeBlock, CodeBlockContent, CodeBlockFooter, CodeBlockHeader, CodeBlockLine, CodeBlockTitle } from '../../components/ui/shadcn/code-block';

/** How many rows of a diff are shown before the rest folds away behind the expander. */
const DIFF_MAX_ROWS = 60;
/** One Tailwind utility per syntax category. Spelled out rather than composed, because Tailwind's source
 *  scanner only emits a utility it can SEE — a `text-code-${kind}` template would compile to nothing. */
const CODE_TOKEN_CLASS: Record<CodeTokenKind, string> = {
  plain: 'text-code-plain',
  keyword: 'text-code-keyword',
  string: 'text-code-string',
  number: 'text-code-number',
  comment: 'text-code-comment',
  type: 'text-code-type',
  function: 'text-code-function',
  punct: 'text-code-punct',
};

/** One row of a display diff, laid out the way the CLI paints it: a line-number column, the `+`/`-`
 *  marker, then the source — the whole row carrying its add/delete tint rather than a left border.
 *
 *  Added and context rows are syntax-coloured; a removed row never is. That is the CLI's rule (and Claude
 *  Code's before it): a dark Monokai token on the red ground is unreadable, so a delete renders plain.
 *  The gutter is unselectable, so copying a diff yields the source and not a column of line numbers, and
 *  it is hidden from assistive technology — a screen reader gets the same information as a spoken word
 *  instead, because a row's `+` used to be part of the text it read out. A long line wraps under the
 *  gutter instead of scrolling sideways.
 *
 *  Memoized: a streaming turn re-renders its whole transcript several times a second, and every one of
 *  those renders would otherwise re-parse and re-lex every visible row of every diff in it. */
const DiffRowView = memo(function DiffRowView({ row, lang, added, removed }: {
  row: string;
  lang: string | null;
  added: string;
  removed: string;
}) {
  const parsed = parseDiffRow(row);
  // Not a diff row at all — a hunk header or a summary line the daemon put in the block.
  if (!parsed) return <CodeBlockLine className="text-code-comment">{row || ' '}</CodeBlockLine>;
  const { sign, num, text } = parsed;
  const tokens = sign === '-' ? null : highlightCode(text, lang);
  return (
    <CodeBlockLine data-diff-sign={sign} data-line-number={num} variant={sign === '+' ? 'added' : sign === '-' ? 'removed' : 'default'}>
      {sign === ' ' ? null : <span className="sr-only">{sign === '+' ? added : removed}</span>}
      <span
        data-testid="chat-diff-code"
        className={`min-w-0 flex-1 whitespace-pre-wrap ${sign === '-' ? 'text-diff-del-foreground' : 'text-code-plain'}`}
      >
        {tokens
          ? tokens.map((token, i) => <span key={i} className={CODE_TOKEN_CLASS[token.kind]}>{token.text}</span>)
          : text || '\u00a0'}
      </span>
    </CodeBlockLine>
  );
});

/** An edit's display diff, rendered as the CLI renders it: syntax-highlighted source on a code canvas,
 *  with the whole row tinted green or red and a line-number gutter, so the same edit reads the same in
 *  the terminal and in the browser. The grammar comes from the tool's own file path, exactly as the CLI
 *  picks one.
 *
 *  A diff longer than the preview folds behind the shared expander instead of being cut off for good: the
 *  rest of an edit is exactly what a reader checking the change needs, and the transcript is the only
 *  place it is shown. Expanded, the block scrolls within its own bounded height rather than growing
 *  without limit, so a thousand-line edit still cannot take over the viewport on a phone. */
export function DiffBlock({ diff, path }: { diff: string; path?: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const bodyId = useId();
  const lang = useMemo(() => langForPath(path), [path]);
  const lines = useMemo(() => diff.replace(/\n+$/, '').split('\n'), [diff]);
  const hiddenRows = Math.max(0, lines.length - DIFF_MAX_ROWS);
  return (
    <CodeBlock size="sm" className="my-1">
      {path ? <CodeBlockHeader><CodeBlockTitle>{path}</CodeBlockTitle></CodeBlockHeader> : null}
        <CodeBlockContent
          id={bodyId}
          data-testid="chat-diff"
          data-numbered=""
          wrap
          className={expanded ? 'max-h-96' : ''}
          role={expanded ? 'group' : undefined}
          aria-label={expanded ? t.brainChat.diffLabel : undefined}
        >
          {(expanded ? lines : lines.slice(0, DIFF_MAX_ROWS)).map((l, i) => (
            <DiffRowView key={i} row={l} lang={lang} added={t.brainChat.diffRowAdded} removed={t.brainChat.diffRowRemoved} />
          ))}
        </CodeBlockContent>
      {hiddenRows > 0 ? (
        <CodeBlockFooter>
          <MorePill
            expanded={expanded}
            hidden={hiddenRows}
            onToggle={() => setExpanded((v) => !v)}
            controls={bodyId}
            // The pill's own "+N more" repeats across every diff in the transcript; spell out what this
            // one unfolds for a reader who only hears the button.
            label={expanded ? t.brainChat.diffCollapse : interpolate(t.brainChat.diffExpand, { n: String(hiddenRows) })}
          />
        </CodeBlockFooter>
      ) : null}
    </CodeBlock>
  );
}
